class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = wcmmgSettings.design_mode;

    this.currentPage = 1;
    this.paginationType; // "paginate" | "loadmore" | "infinite"
    this.totalPages;
    this.totalProducts;
    this.infiniteLoadingObserver;

    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType; // "storefront_filters" | "tags_filter"
    this.filtersPosition; // "leftColumn" | "rightColumn" | "fixed"
    this.filterLayout; // "sidebar" | "topbar"
    this.accordions;
    this.sideEffectEventsAdded = false;
    this.filterEventsAdded = false;
    this.sortingEventsAdded = false;
    this.topbarFilterEventsAdded = false;
    this.sidebarOverlayHandlerAdded = false;
    this.sortingOverlayHandlerAdded = false;
    this.topbarOutsideHandlerAdded = false;
    this.escapeHandlerAdded = false;
    this.stickyToolbarScrollHandler = null;
    this.lastToolbarScroll = 0;
    this.toolbarScrollTicking = false;
  }

  init = () => {
    const shouldBindSideEffects = !this.sideEffectEventsAdded;

    const section = document.querySelector(this.selectors.section);
    if (!section) return;

    this.domNodes = {
      section,
      toolbar: section.querySelector(this.selectors.toolbar),
      cols: [...section.querySelectorAll(this.selectors.cols[0])],
      productContainer: section.querySelector(this.selectors.productContainer),
      loadMoreBtn: section.querySelector(this.selectors.loadMoreBtn),
      sidebar: section.querySelector(this.selectors.sidebar),
      sidebarContent: section.querySelector(this.selectors.sidebarContent),
      openSidebar: section.querySelector(this.selectors.openSidebar),
      closeSidebar: section.querySelector(this.selectors.closeSidebar),
      filtersWrapper: section.querySelector(this.selectors.filtersWrapper),
    };
    this.mbSortingNodes = {
      openSorting: section.querySelector(this.mobileSortingSelectors.openSorting),
      closeSorting: section.querySelector(this.mobileSortingSelectors.closeSorting),
      sortingWrapper: section.querySelector(this.mobileSortingSelectors.sortingWrapper),
      sortingList: section.querySelector(this.mobileSortingSelectors.sortingList),
    };
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();

    if (shouldBindSideEffects) {
      wcmmgEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());
      this.handleStickyToolbar();
      document.addEventListener("matchMobile", () => this.handleStickyToolbar());
      document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
    }

    this.sideEffectEventsAdded = true;
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    // Grid view data
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    // TODO: get from URL Search Params
    this.activeCol = null;
    this.currentPage = 1;
    this.initialCol = Number(section.dataset.initialColumn);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    // Filters data
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
    this.filterLayout = section.dataset.filterLayout || "sidebar";
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column)),
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, sidebarContent, openSidebar, closeSidebar, filtersWrapper } = this.domNodes;
    const isMobileTopbar = this.filterLayout === "topbar" && window.innerWidth < 1024;
    if (this.filterLayout !== "topbar" || isMobileTopbar) {
      window.requestAnimationFrame(this.initAccordions);
    } else if (filtersWrapper) {
      filtersWrapper.style.setProperty("opacity", "1");
    }

    if (openSidebar && !openSidebar.dataset.collectionSidebarBound) {
      openSidebar.addEventListener("click", (e) => {
        e.preventDefault();
        this.openSidebarFilter();
      });
      openSidebar.dataset.collectionSidebarBound = "true";
    }

    if (closeSidebar && !closeSidebar.dataset.collectionSidebarCloseBound) {
      closeSidebar.addEventListener("click", (e) => {
        e.preventDefault();
        this.closeSidebarFilter();
      });
      closeSidebar.dataset.collectionSidebarCloseBound = "true";
    }

    if (this.filterLayout === "topbar" && sidebarContent && !sidebarContent.dataset.collectionTopbarBound) {
      sidebarContent.addEventListener("click", (e) => {
        if (window.innerWidth < 1024) return;
        const trigger = e.target.closest(".m-filter--widget-title");
        if (!trigger || !sidebarContent.contains(trigger)) return;

        e.preventDefault();
        const widget = trigger.closest(".m-filter--widget");
        const content = widget && widget.querySelector(".m-filter--widget-content");
        if (!widget || !content) return;

        const isOpen = widget.classList.contains("open");
        this.closeTopbarFilters(widget);
        widget.classList.toggle("open", !isOpen);
      });
      sidebarContent.dataset.collectionTopbarBound = "true";
      this.topbarFilterEventsAdded = true;
    }

    if (this.filterLayout === "topbar" && !this.topbarOutsideHandlerAdded) {
      document.addEventListener("click", (e) => {
        const currentSidebar = this.domNodes && this.domNodes.sidebar;
        if (!currentSidebar || this.filterLayout !== "topbar") return;
        if (currentSidebar.contains(e.target)) return;
        this.closeTopbarFilters();
      });
      this.topbarOutsideHandlerAdded = true;
    }

    if (!this.escapeHandlerAdded) {
      document.addEventListener("keydown", (e) => {
        if (e.key !== "Escape") return;
        this.closeTopbarFilters();
        this.closeMobileSorting();
        this.closeSidebarFilter();
      });
      this.escapeHandlerAdded = true;
    }

    if (!this.filterEventsAdded) {
      this.filterEventsAdded = true;
    }

    if (sidebar && !this.sidebarOverlayHandlerAdded) {
      sidebar.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
      this.sidebarOverlayHandlerAdded = true;
    }
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;

    if (openSorting && !openSorting.dataset.collectionSortingBound) {
      openSorting.addEventListener("click", (e) => {
        e.preventDefault();
        this.openMobileSorting();
      });
      openSorting.dataset.collectionSortingBound = "true";
    }

    if (closeSorting && !closeSorting.dataset.collectionSortingCloseBound) {
      closeSorting.addEventListener("click", (e) => {
        e.preventDefault();
        this.closeMobileSorting();
      });
      closeSorting.dataset.collectionSortingCloseBound = "true";
    }

    if (!this.sortingEventsAdded) {
      this.sortingEventsAdded = true;
    }

    if (sortingWrapper && !this.sortingOverlayHandlerAdded) {
      sortingWrapper.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
      this.sortingOverlayHandlerAdded = true;
    }
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    if (!sortingWrapper || !sortingList) return;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = (e) => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    if (!sortingWrapper || !sortingList) return;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  closeTopbarFilters = (exceptWidget = null) => {
    if (this.filterLayout !== "topbar" || !this.domNodes?.section) return;
    this.domNodes.section.querySelectorAll(".m-sidebar--topbar .m-filter--widget.open").forEach((widget) => {
      if (widget !== exceptWidget) widget.classList.remove("open");
    });
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    if (!filtersWrapper) return;
    if (this.filterLayout === "topbar" && window.innerWidth < 1024) {
      filtersWrapper.querySelectorAll(".m-accordion--item").forEach((item) => item.classList.add("open"));
      filtersWrapper.querySelectorAll(".m-accordion--item-content").forEach((content) => {
        content.style.opacity = "1";
      });
    }
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new wcmmgLibs.Accordion(filtersWrapper, {
      presetContentHeight: this.filterLayout !== "topbar" && window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
    if (this.filterLayout === "topbar" && window.innerWidth < 1024) {
      window.requestAnimationFrame(() => this.accordions && this.accordions.setContentHeight());
    }
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    if (!sidebar || !sidebarContent || !section) return;
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    if (!sidebar || !sidebarContent || !section) return;
    section.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
    }
  };

  toggleView = (col) => {
    if (!col || this.view === "search") return;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;
    const activeCol = this.activeCol || this.initialCol;
    if (this.activeCol === col && productContainer.classList.contains(`m-cols-${col}`)) return;

    if (this.showColSwitchers && cols[activeCol - 1] && cols[col - 1]) {
      cols[activeCol - 1].classList.remove("active");
      cols[col - 1].classList.add("active");
    }

    productContainer.classList.remove(`m-cols-${activeCol}`);
    productContainer.classList.add(`m-cols-${col}`);

    window.localStorage.setItem(this.STORAGE_KEY, col);
    this.activeCol = col;

    window.requestAnimationFrame(() => {
      productContainer.querySelectorAll("responsive-image img").forEach((img) => {
        const imageWrapper = img.closest("responsive-image");
        if (imageWrapper && imageWrapper.clientWidth) {
          img.sizes = `${Math.round(imageWrapper.clientWidth)}px`;
        }
      });
    });
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return; // List layout
    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];
    if (maxCol < this.activeCol) this.toggleView(maxCol);
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    this.toggleLoading(true);
    fetchSection(this.sectionId, { fromCache: true, params: { page: nextPage } })
      .then((productGridHTML) => {
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => productContainer.appendChild(prodNode));
        this.initProducts();
      })
      .catch((err) => console.error(`Failed to load more products.`, err))
      .finally(() => {
        wcmmgEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;
        if (nextPage >= this.totalPages) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
        }
      });
  };

  initProducts = () => {
    wcmmgTheme.CompareProduct && wcmmgTheme.CompareProduct.setCompareButtonsState();
    wcmmgTheme.Wishlist && wcmmgTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const method = loading ? "add" : "remove";
      loadMoreBtn.classList[method]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    if (!toolbar) return;

    if (this.stickyToolbarScrollHandler) {
      window.removeEventListener("scroll", this.stickyToolbarScrollHandler);
    }

    const updateToolbar = () => {
      const extraSpace = toolbar.offsetTop || 0;
      if (wcmmgTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > this.lastToolbarScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < this.lastToolbarScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        this.lastToolbarScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    };

    this.stickyToolbarScrollHandler = () => {
      if (this.toolbarScrollTicking) return;
      this.toolbarScrollTicking = true;
      window.requestAnimationFrame(() => {
        updateToolbar();
        this.toolbarScrollTicking = false;
      });
    };

    window.addEventListener("scroll", this.stickyToolbarScrollHandler, { passive: true });
    updateToolbar();
  }
}

// Update collection prices for the selected bedding size without replacing cards.
(function() {
  const productCache = new Map();
  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  });

  function isSizeKey(value) {
    return value && value.toLowerCase().includes("size");
  }

  function getSizeFromUrl() {
    let selectedSize = null;
    new URLSearchParams(location.search).forEach((value, key) => {
      if (isSizeKey(key)) selectedSize = value;
    });
    return selectedSize;
  }

  function ensureSingleSizeInURL(currentSize) {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const keysToDelete = [];

    params.forEach((_, key) => {
      if (isSizeKey(key)) keysToDelete.push(key);
    });
    keysToDelete.forEach((key) => params.delete(key));

    if (currentSize) params.append("filter.v.option.bedding size", currentSize);
    window.history.replaceState({}, "", url.toString());
  }

  function fetchProductData(handle) {
    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`/products/${handle}.js`)
          .then((response) => response.json())
          .catch((error) => {
            productCache.delete(handle);
            throw error;
          })
      );
    }
    return productCache.get(handle);
  }

  function findPriceElement(product) {
    const priceSelectors = [
      ".m-product-card__price .m-price",
      ".m-product-card__price",
      ".m-product-price",
      ".price",
      ".product-card__price",
      ".price-item",
      ".price__regular",
      ".money",
      "[data-product-price]",
      ".current-price",
      ".price--wrapper",
      ".product-card__price-value",
    ];

    for (const selector of priceSelectors) {
      const priceElement = product.querySelector(selector);
      if (priceElement) return priceElement;
    }

    return null;
  }

  function setPriceText(priceElement, formattedPrice) {
    const moneyEl = priceElement.querySelector(".money, .price-item, span");
    if (moneyEl) {
      moneyEl.textContent = formattedPrice;
      return;
    }
    priceElement.textContent = formattedPrice;
  }

  function updatePricesForSize(size) {
    if (!size) return;
    window.selectedSize = size;

    document.querySelectorAll(".m-product-item, .product-item").forEach((product) => {
      const link = product.querySelector('a[href*="/products/"]');
      const handle = link && link.getAttribute("href").split("/products/")[1]?.split("?")[0].split("#")[0];
      if (!handle) return;

      fetchProductData(handle)
        .then((productData) => {
          const variant = productData.variants.find((item) => (
            item.option1 === size || item.option2 === size || item.option3 === size
          ));
          if (!variant) return;

          const priceElement = findPriceElement(product);
          if (priceElement) setPriceText(priceElement, formatter.format(variant.price / 100));
        })
        .catch((error) => console.warn("Unable to update collection price", error));
    });
  }

  window.updatePricesForSize = updatePricesForSize;

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!target.name || !isSizeKey(target.name)) return;

    const isChoiceInput = target.type === "checkbox" || target.type === "radio";
    const shouldApply = !isChoiceInput || target.checked;
    const selectedSize = shouldApply ? target.value : null;

    if (shouldApply) {
      document.querySelectorAll("input").forEach((input) => {
        if (input !== target && isSizeKey(input.name)) input.checked = false;
      });
    }

    ensureSingleSizeInURL(selectedSize);
    if (selectedSize) window.setTimeout(() => updatePricesForSize(selectedSize), 300);
  });

  document.addEventListener("submit", (event) => {
    if (event.target.id !== "CollectionFiltersForm") return;

    const formData = new FormData(event.target);
    let selectedSize = null;
    for (const [key, value] of formData.entries()) {
      if (isSizeKey(key)) {
        selectedSize = value;
        break;
      }
    }
    if (selectedSize) window.setTimeout(() => updatePricesForSize(selectedSize), 600);
  });

  let lastUrl = location.href;
  function handleUrlChange() {
    if (location.href === lastUrl) return;
    lastUrl = location.href;

    const selectedSize = getSizeFromUrl();
    if (selectedSize) window.setTimeout(() => updatePricesForSize(selectedSize), 300);
  }

  new MutationObserver(handleUrlChange).observe(document, { subtree: true, childList: true });
  window.addEventListener("popstate", handleUrlChange);

  window.addEventListener("load", () => {
    const selectedSize = getSizeFromUrl();
    if (selectedSize) {
      window.selectedSize = selectedSize;
      window.setTimeout(() => updatePricesForSize(selectedSize), 600);
    }
  });
})();
